import {
    convertNetworkTypeToTelemetryType,
    IEventEmitter,
    TelemetryEventPayload,
    GSModuleName,
    IPVersion,
    GS_ExceptionInfoDef,
    GS_DebugInfoDef,
    GS_ClientMetricEventDef,
    GS_Sleep_EventDef,
    GS_FeatureDef,
    FeatureSupportStatus,
    Log,
    GS_WsCallResultDef,
    TelemetryImpl,
    TelemetryEventBase
} from "../dependencies";
import {
    RagnarokTelemetryConfig,
    Streamer_StartDef,
    Streamer_InputDeviceDef,
    InputDeviceType,
    Streamer_ExitDef
} from "./telemetryinterfaces";
import { EVENTS, clientShutDownCallbackType } from "../interfaces";
import { TelemetryEventProcessor } from "./telemetryeventprocessor";
import { StreamExitEventData } from "../rinterfaces";
import { RagnarokSettings } from "../util/settings";
import { toTelemetryVideoCodecType, ToBooleanType, ToResumeType } from "../util/utils";
import { NetworkDetector } from "../util/networkdetector";
import { VideoCodecType } from "../nskinterfaces";

const LOGTAG = "telemetryhandler";

/**
 * Interface passed to all the Ragnarok classes for handling telemetry generated by the internal objects.
 */
export interface TelemetryHandler {
    // For all the functions please refer to the documentation of the parameters for corresponding events in telemetry schema.

    emitExceptionEvent(
        error: Error | DOMException | undefined,
        msg: string,
        file: string,
        lineno: number,
        colno: number,
        handled: boolean,
        category?: string
    ): void;

    /**
     * Sends a WsCallResult telemetry event.
     * @param url - websocket connection url.
     * @param statusCode - code received from server when connection was closed.
     * @param serverId - server/haproxy instance in GFN where web socket connection was established.
     * @param disconnectionReason - server provided disconnection reason.
     * @param retryCount - retry number of the current websocket connection.
     * @param cleanDisconnection - optional, indicates if disconnection from server was clean or errored out.
     */
    emitGsWebSocketEvent(
        url: string,
        statusCode: number,
        serverId: string,
        disconnectionReason: string,
        retryCount: number,
        cleanDisconnection: boolean | undefined
    ): void;

    emitGsFeatureEvent(
        featureName: string,
        supported: boolean,
        defaultEnabled: boolean,
        enabled: boolean,
        reason: string
    ): void;

    emitDebugEvent(key1?: string, key2?: string, key3?: string, key4?: string, key5?: string): void;

    emitMetricEvent(
        metricName: string,
        valueString: string,
        valueDouble: number,
        valueInt1: number,
        valueInt2: number,
        valueInt3: number
    ): void;

    emitGamepadEvent(
        gamepadName: string,
        vid: string,
        pid: string,
        index: number,
        hapticsSupported: boolean,
        hapticsFeedbackCount: number,
        primary: boolean,
        state: number,
        eventMap: string
    ): void;

    emitSleepEvent(
        sleepTime: number,
        timeToSleep: number,
        sleepSequence: string,
        error: string,
        sessionId: string,
        subSessionId: string
    ): void;
}

/**
 * Implements the telemetry support for Ragnarok2.0.
 * This class just caches the session specific data, constructs the individual telemetry events based on the parameters + telemetryEventIds + cached session data
 * and lets the TelemetryImpl base class process the events.
 */
class RagnarokTelemetryHandler extends TelemetryImpl implements TelemetryHandler {
    protected sessionId: string = "";
    protected subSessionId: string = "";
    protected cmsId: string = "";
    private isResume: boolean = false;
    private zoneAddress: string = "";
    protected totalGamepadEventsCount: number = 0;
    // todo: move all the exception tracking code to base class.
    protected exceptionCounts: Map<string, number> = new Map<string, number>();
    protected totalExceptionCount: number = 0;

    constructor() {
        super(RagnarokTelemetryConfig);
    }

    /**
     * This should be invoked when clients have requested for new session.
     * The values passed here will be added to the appropriate events (Stream_Start/Stream_Exit/DebugEvent etc)
     * @param subSessionId
     * @param sessionId
     * @param cmsId
     * @param isResume
     * @param zoneAddress
     */
    public setSessionData(
        subSessionId: string,
        sessionId: string,
        cmsId: string,
        isResume: boolean,
        zoneAddress: string
    ) {
        this.subSessionId = subSessionId;
        this.sessionId = sessionId;
        this.cmsId = cmsId;
        this.isResume = isResume;
        this.zoneAddress = zoneAddress;
    }

    /**
     * Clears the session properties provided in setSessionData API.
     * Should be invoked after sendStreamStartEvent and sendStreamExitEvent as they use cached session properties in event generation.
     */
    public resetSessionData() {
        this.subSessionId = "";
        this.sessionId = "";
        this.cmsId = "";
        this.isResume = false;
        this.zoneAddress = "";
        this.totalGamepadEventsCount = 0;
        this.exceptionCounts.clear();
        this.totalExceptionCount = 0;
    }

    /**
     * Generates Streamer_Start event data and processes it.
     * Callers must invoke this after setSessionData API.
     * @param launchDuration - streaming start duration.
     * @param result
     * @param codec
     */
    public sendStreamStartEvent(launchDuration: number, result: string, codec: VideoCodecType) {
        const event: Streamer_StartDef = new Streamer_StartDef({
            zoneAddress: this.zoneAddress,
            networkSessionId: this.telemetryEventIds.networkTestId ?? "",
            sessionId: this.sessionId,
            subSessionId: this.subSessionId,
            resumeType: ToResumeType(this.isResume),
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            result: result,
            codec: toTelemetryVideoCodecType(codec),
            ipVersion: IPVersion.UNKNOWN,
            launchDuration: Math.round(launchDuration),
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            streamingProfileGuid: this.telemetryEventIds.streamingProfileGuid ?? "",
            systemInfoGuid: this.telemetryEventIds.systemInfoGuid ?? "",
            cmsId: this.cmsId
        });
        this.processEvent(event);
    }

    /**
     * Sends Streamer_Exit exit.
     * Callers must invoked this after setSessionData API, before resetSessionData API.
     * @param result
     * @param exitReason
     * @param duration
     * @param codec
     * @param frameCount
     */
    public sendStreamExitEvent(
        result: string,
        exitReason: string,
        duration: number,
        codec: VideoCodecType,
        frameCount: number
    ) {
        const event: Streamer_ExitDef = new Streamer_ExitDef({
            zoneAddress: this.zoneAddress,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId,
            resumeType: ToResumeType(this.isResume),
            cmsId: this.cmsId,
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            exitReason: exitReason,
            result: result,
            frameCount: frameCount,
            codec: toTelemetryVideoCodecType(codec),
            ipVersion: IPVersion.UNKNOWN,
            streamDuration: Math.round(duration),
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            streamingProfileGuid: this.telemetryEventIds.streamingProfileGuid ?? "",
            systemInfoGuid: this.telemetryEventIds.systemInfoGuid ?? "",
            networkSessionId: this.telemetryEventIds.networkTestId ?? ""
        });
        this.processEvent(event, true);
    }

    public emitExceptionEvent(
        error: Error | DOMException | undefined,
        msg: string,
        file: string,
        lineno: number,
        colno: number,
        handled: boolean,
        category?: string
    ): void {
        if (!this.canSendExceptionEvent(msg)) {
            return;
        }
        if (error instanceof DOMException) {
            error = { name: error.name, message: error.message };
        }

        const event = new GS_ExceptionInfoDef({
            filename: file,
            lineno: lineno,
            stacktrace: error?.stack ?? "",
            colno: colno,
            handled: ToBooleanType(handled),
            category: category ?? "",
            message: msg,
            moduleName: GSModuleName.RAGNAROK,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public emitGsWebSocketEvent(
        url: string,
        statusCode: number,
        serverId: string,
        disconnectionReason: string,
        retryCount: number,
        cleanDisconnection: boolean | undefined
    ): void {
        const event: GS_WsCallResultDef = new GS_WsCallResultDef({
            retryCount: retryCount,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId,
            serverId: serverId,
            url: url,
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            disconnectionReason: disconnectionReason,
            cleanDisconnection: ToBooleanType(cleanDisconnection),
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            statusCode: statusCode ? statusCode.toString() : "",
            cmsId: String(this.cmsId),
            moduleName: GSModuleName.RAGNAROK
        });
        this.processEvent(event);
    }

    public emitGsFeatureEvent(
        featureName: string,
        supported: boolean,
        defaultEnabled: boolean,
        enabled: boolean,
        reason: string
    ): void {
        const event: GS_FeatureDef = new GS_FeatureDef({
            featureName: featureName,
            supported: supported
                ? FeatureSupportStatus.SUPPORTED
                : FeatureSupportStatus.UNSUPPORTED,
            defaultEnabled: ToBooleanType(defaultEnabled),
            enabled: ToBooleanType(enabled),
            reason: reason,
            moduleName: GSModuleName.RAGNAROK,
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public emitDebugEvent(
        key1?: string,
        key2?: string,
        key3?: string,
        key4?: string,
        key5?: string
    ): void {
        const event: GS_DebugInfoDef = new GS_DebugInfoDef({
            key1: key1 ?? "",
            key2: key2 ?? "",
            key3: key3 ?? "",
            key4: key4 ?? "",
            key5: key5 ?? "",
            moduleName: GSModuleName.RAGNAROK,
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            cmsId: String(this.cmsId),
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public emitMetricEvent(
        metricName: string,
        valueString: string,
        valueDouble: number,
        valueInt1: number,
        valueInt2: number,
        valueInt3: number
    ): void {
        const event: GS_ClientMetricEventDef = new GS_ClientMetricEventDef({
            metricName: metricName,
            moduleName: GSModuleName.RAGNAROK,
            valueInt1: valueInt1,
            valueInt2: valueInt2,
            valueInt3: valueInt3,
            valueString: valueString,
            valueDouble: valueDouble,
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            cmsId: String(this.cmsId),
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public emitGamepadEvent(
        gamepadName: string,
        vid: string,
        pid: string,
        index: number,
        hapticsSupported: boolean,
        hapticsFeedbackCount: number,
        primary: boolean,
        state: number,
        eventMap: string
    ): void {
        const MAX_GAMEPAD_EVENTS_TOTAL: number = 50;

        if (this.totalGamepadEventsCount >= MAX_GAMEPAD_EVENTS_TOTAL) {
            return;
        }
        this.totalGamepadEventsCount++;
        const event: Streamer_InputDeviceDef = new Streamer_InputDeviceDef({
            deviceName: gamepadName,
            deviceType: InputDeviceType.GAMEPAD,
            vendorId: vid,
            productId: pid,
            deviceIndex: index,
            reportIndex: primary ? 0 : index,
            hapticsSupported: ToBooleanType(hapticsSupported),
            hapticsFeedbackCount: hapticsFeedbackCount,
            state: state,
            eventMapReceived: eventMap,
            eventMapProcessed: eventMap,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId,
            cmsId: String(this.cmsId)
        });
        this.processEvent(event);
    }

    public emitSleepEvent(
        sleepTime: number,
        timeToSleep: number,
        sleepSequence: string,
        error: string,
        sessionId: string,
        subSessionId: string
    ): void {
        const event: GS_Sleep_EventDef = new GS_Sleep_EventDef({
            eventSequence: sleepSequence,
            sleepTime: sleepTime,
            error: error,
            timeToSleep: timeToSleep,
            moduleName: GSModuleName.RAGNAROK,
            cmsId: String(this.cmsId),
            sessionId: sessionId,
            subSessionId: subSessionId
        });
        this.processEvent(event);
    }

    /**
     * todo: move this to base class.
     * @param message
     * @returns
     */
    private canSendExceptionEvent(message: string) {
        const MAX_EXCEPTIONS_PER_MSG: number = 10;
        const MAX_EXCEPTIONS_TOTAL: number = 50;

        if (this.totalExceptionCount >= MAX_EXCEPTIONS_TOTAL) {
            return false;
        }
        const count = this.exceptionCounts.get(message) ?? 0;
        if (count >= MAX_EXCEPTIONS_PER_MSG) {
            return false;
        }
        this.totalExceptionCount++;
        this.exceptionCounts.set(message, count + 1);
        return true;
    }
}

// Singleton instance exposed to clients from nskutils.
export const StreamKitTelemetryHandler = new RagnarokTelemetryHandler();

//--------------------- Ragnarok 1.0 class ---------------------------------------------

/**
 * Provides telemetry handling for Ragnarok1.0.
 * The functionality of telemetry caching and uploading is implemented in TelemetryProcessor.
 * Delete this during cleanup of Ragnarok1.0 code.
 * This class overrides the processEvent of base class to support emitting telemetry event.
 */
export class LegacyTelemetryHandler extends RagnarokTelemetryHandler {
    private eventEmitter: IEventEmitter;
    private telemetryEventProcessor: TelemetryEventProcessor;
    private clientShutDownCallback?: clientShutDownCallbackType;

    constructor(_eventEmitter: IEventEmitter, telemetryEventProcessor: TelemetryEventProcessor) {
        super();
        this.eventEmitter = _eventEmitter;
        this.telemetryEventProcessor = telemetryEventProcessor;
        this.telemetryEventProcessor.setExceptionHandler(this.emitExceptionEvent.bind(this));
    }

    public setClientShutDownCallback(clientShutDownCallback?: clientShutDownCallbackType) {
        this.clientShutDownCallback = clientShutDownCallback;
    }

    public dispatchEvent(event: TelemetryEventPayload) {
        if (this.eventEmitter.hasListener(EVENTS.TELEMETRY_EVENT)) {
            // Emit the event to the upper layer
            this.eventEmitter.emit(EVENTS.TELEMETRY_EVENT, event);
        } else {
            this.telemetryEventProcessor.sendTelemetryEvent(event);
        }
    }

    public setSessionId(sessionId: string) {
        this.sessionId = sessionId;
        this.totalGamepadEventsCount = 0;
    }

    public setSubSessionId(subSessionId: string) {
        this.subSessionId = subSessionId;
        this.exceptionCounts.clear();
        this.totalExceptionCount = 0;
    }

    public setGameDetails(cmsId: string, name: string) {
        this.telemetryEventProcessor.setGameDetails(cmsId, name);
    }

    public emitLaunchEvent(
        sessionId: string,
        subSessionId: string,
        isResume: boolean,
        zoneAddress: string,
        launchDuration: number,
        result: string,
        codec: VideoCodecType,
        cmsId: string,
        networkSessionId: string
    ) {
        const launchAnalyticsEvent: Streamer_StartDef = new Streamer_StartDef({
            zoneAddress: zoneAddress,
            networkSessionId: networkSessionId,
            sessionId: sessionId,
            subSessionId: subSessionId,
            resumeType: ToResumeType(isResume),
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            result: result,
            codec: toTelemetryVideoCodecType(codec),
            ipVersion: IPVersion.UNKNOWN,
            launchDuration: Math.round(launchDuration),
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            streamingProfileGuid: this.telemetryEventProcessor.getStreamingProfileGuid(),
            systemInfoGuid: this.telemetryEventProcessor.getSystemInfoGuid(),
            cmsId: cmsId
        });
        this.dispatchEvent({
            name: launchAnalyticsEvent.name,
            gdprLevel: launchAnalyticsEvent.gdprLevel,
            parameters: launchAnalyticsEvent.parameters,
            ts: launchAnalyticsEvent.ts,
            clientConfig: RagnarokTelemetryConfig
        });
    }

    public override processEvent(
        event: TelemetryEventBase<any>,
        priority?: boolean | undefined
    ): void {
        this.dispatchEvent({
            name: event.name,
            gdprLevel: event.gdprLevel,
            parameters: event.parameters,
            ts: event.ts,
            clientConfig: RagnarokTelemetryConfig
        });
    }

    /**
     * Exit event handling follows the below precedence:
     * 1. If the client has registered for ShutDownCallback, we call it with the eventPayload
     * 2. If ShutDownCallback not available or fails to send the event, send the Exit event using beacon API
     */
    private processExitEvent(eventPayload: TelemetryEventPayload) {
        let eventSent = false;
        if (this.clientShutDownCallback) {
            eventSent = this.clientShutDownCallback(eventPayload);
            Log.d("{9838627}", "{caf81d9}", eventSent);
        }
        if (!eventSent) {
            Log.d("{9838627}", "{e939db1}");
            this.telemetryEventProcessor.sendExitEvent(eventPayload);
        }
    }

    public sendExitAnalyticsEvent(exitEventData: StreamExitEventData, pollingDone: boolean) {
        let eventPayload: TelemetryEventPayload;
        eventPayload = this.telemetryEventProcessor.getStreamerExitEventPayload(exitEventData);
        this.processExitEvent(eventPayload);
        this.telemetryEventProcessor.clearExitEventStore(pollingDone);
    }

    // We cache the exit events periodically. If the client has opted to handle exit events (registered clientShutdownCallback),
    // we should relay these back to the client. If not we send them from within
    public sendCachedExitEvent(pollingDone: boolean): Promise<void> {
        if (this.clientShutDownCallback) {
            Log.d("{9838627}", "{c7dcc4e}");
            return this.telemetryEventProcessor
                .getCachedExitEvents()
                .then(exitEventPayloads => {
                    for (const payload of exitEventPayloads) {
                        this.processExitEvent(payload);
                    }
                    this.telemetryEventProcessor.clearExitEventStore(pollingDone);
                })
                .catch(() => {
                    Log.d("{9838627}", "{41cf828}");
                });
        } else {
            Log.d("{9838627}", "{77df598}");
            return this.telemetryEventProcessor.sendCachedExitEvent(pollingDone);
        }
    }
}
